import { useMemo } from 'react';

import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import { useAssociationMatchers } from '@/lib/crm/hooks/useAssociationMatchers';
import { HIDDEN_ASSOCIATION_TARGETS } from '@/lib/crm/record/registry/fieldConstants';
import type { CrmObject } from '@/types/crm';

export interface VisibleAssoc {
  id: string;
  assocType: string;
  targetId: string;
}

export interface AssocGroup {
  assocType: string;
  label: string;
  /**
   * True when this is a `*_to_one` type whose canonical and source records
   * point at DIFFERENT targets. The merged record can keep only one, so the
   * user must pick — rendered as a radio group instead of a dismissable list.
   */
  isToOneConflict: boolean;
  /** Canonical record's existing target (the default pick). */
  canonicalTarget: string | null;
  /** Source record's target. */
  sourceTarget: string | null;
  /** All of the canonical record's targets for this type. */
  canonicalTargets: string[];
  /** All of the source record's targets for this type. */
  sourceTargets: string[];
  /** Deduped union of targets, for the dismissable-list rendering. */
  items: VisibleAssoc[];
}

/** A single association target categorised for review (auto-resolved / matching). */
export interface AssocReviewItem {
  /** `${assocType}:${targetId}` — matches the dismissed-association key. */
  id: string;
  assocType: string;
  label: string;
  targetId: string;
}

/** Associations split into the same three categories as properties. */
export interface CategorisedAssociations {
  /** `*_to_one` types whose two records point at different targets — pick one. */
  conflicts: AssocGroup[];
  /** Targets present on only one side — transfer automatically (dismissable). */
  autoResolved: AssocReviewItem[];
  /** Targets present on both records — kept as-is, nothing to decide. */
  matching: AssocReviewItem[];
}

/**
 * Visible associations split into conflicts / auto-resolved / matching, mirroring
 * the property resolution categories. Shared by Step 2's unified sections.
 */
export function useCategorisedAssociations(
  canonicalObject: CrmObject | undefined,
  sourceObject: CrmObject | undefined,
  orgId: number
): CategorisedAssociations & { isLoading: boolean } {
  const { groups, isLoading } = useVisibleAssociationGroups(
    canonicalObject,
    sourceObject,
    orgId
  );
  const categorised = useMemo(() => categoriseAssociations(groups), [groups]);
  return { ...categorised, isLoading };
}

/**
 * Split visible association groups into conflicts / auto-resolved / matching,
 * mirroring the property resolution categories so Step 2 can present both data
 * types under unified section headings.
 */
export function categoriseAssociations(
  groups: AssocGroup[]
): CategorisedAssociations {
  const conflicts: AssocGroup[] = [];
  const autoResolved: AssocReviewItem[] = [];
  const matching: AssocReviewItem[] = [];

  for (const group of groups) {
    if (group.isToOneConflict) {
      conflicts.push(group);
      continue;
    }
    const canonicalSet = new Set(group.canonicalTargets);
    const sourceSet = new Set(group.sourceTargets);
    for (const item of group.items) {
      const onBoth =
        canonicalSet.has(item.targetId) && sourceSet.has(item.targetId);
      const reviewItem: AssocReviewItem = {
        id: item.id,
        assocType: group.assocType,
        label: group.label,
        targetId: item.targetId,
      };
      if (onBoth) {
        matching.push(reviewItem);
      } else {
        autoResolved.push(reviewItem);
      }
    }
  }

  return { conflicts, autoResolved, matching };
}

/**
 * Build the reviewable association groups shared by Step 2 (interactive) and
 * Step 3 (read-only). Hides system plumbing: unknown types, internal-read
 * types (e.g. enrichment requests), and HIDDEN_ASSOCIATION_TARGETS.
 */
export function useVisibleAssociationGroups(
  canonicalObject: CrmObject | undefined,
  sourceObject: CrmObject | undefined,
  orgId: number
): { groups: AssocGroup[]; isLoading: boolean } {
  const { associationTypes, isLoading } = useAssociationMatchers(orgId);

  const associationTypeMap = useMemo(() => {
    const map = new Map<string, AssociationType>();
    for (const at of associationTypes) {
      map.set(at.association_type_name, at);
    }
    return map;
  }, [associationTypes]);

  const groups = useMemo<AssocGroup[]>(() => {
    const canonicalByType = new Map<string, string[]>();
    const sourceByType = new Map<string, string[]>();

    const collect = (
      assocs:
        | Array<{ association_type_name: string; target_entity_id: string }>
        | undefined,
      into: Map<string, string[]>
    ) => {
      for (const a of assocs ?? []) {
        const at = associationTypeMap.get(a.association_type_name);
        if (!at || at.value_read_access === 'internal') {
          continue;
        }
        // WORKAROUND until #12411: see fieldConstants.ts HIDDEN_ASSOCIATION_TARGETS
        if (
          at.target_entity_type_name &&
          HIDDEN_ASSOCIATION_TARGETS.has(at.target_entity_type_name)
        ) {
          continue;
        }
        const arr = into.get(a.association_type_name) ?? [];
        arr.push(a.target_entity_id);
        into.set(a.association_type_name, arr);
      }
    };
    collect(canonicalObject?.associations, canonicalByType);
    collect(sourceObject?.associations, sourceByType);

    const allTypes = new Set<string>([
      ...canonicalByType.keys(),
      ...sourceByType.keys(),
    ]);

    const out: AssocGroup[] = [];
    for (const assocType of allTypes) {
      const at = associationTypeMap.get(assocType);
      const label = at?.label ?? assocType;
      const isToOne =
        at?.cardinality === 'one_to_one' || at?.cardinality === 'many_to_one';
      const canonTargets = canonicalByType.get(assocType) ?? [];
      const srcTargets = sourceByType.get(assocType) ?? [];

      const seen = new Set<string>();
      const items: VisibleAssoc[] = [];
      for (const targetId of [...canonTargets, ...srcTargets]) {
        const id = `${assocType}:${targetId}`;
        if (seen.has(id)) {
          continue;
        }
        seen.add(id);
        items.push({ id, assocType, targetId });
      }

      const canonicalTarget = canonTargets[0] ?? null;
      const sourceTarget = srcTargets[0] ?? null;
      const isToOneConflict =
        isToOne &&
        canonicalTarget !== null &&
        sourceTarget !== null &&
        canonicalTarget !== sourceTarget;

      out.push({
        assocType,
        label,
        isToOneConflict,
        canonicalTarget,
        sourceTarget,
        canonicalTargets: canonTargets,
        sourceTargets: srcTargets,
        items,
      });
    }
    out.sort((a, b) => a.assocType.localeCompare(b.assocType));
    return out;
  }, [
    canonicalObject?.associations,
    sourceObject?.associations,
    associationTypeMap,
  ]);

  return { groups, isLoading };
}

/**
 * The targets a group will end up with after the user's choices: the picked
 * winner for `*_to_one` conflicts, or the union minus dismissed targets.
 */
export function finalAssociationTargets(
  group: AssocGroup,
  associationChoices: Record<string, string>,
  dismissedAssociationIds: ReadonlySet<string>
): string[] {
  if (group.isToOneConflict && group.canonicalTarget) {
    return [associationChoices[group.assocType] ?? group.canonicalTarget];
  }
  return group.items
    .map(item => item.targetId)
    .filter(
      targetId => !dismissedAssociationIds.has(`${group.assocType}:${targetId}`)
    );
}
